import { NoPetsEmptyState } from '$features/clients/components/no-pets-empty-state'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { clientShowOptions } from '$api-client/@tanstack/react-query.gen'
import { AnimatedOutlet } from '$components'
import { usePageTitle } from '$components'
import { ClientsFilterParam } from '$features/clients/enums/filter-params'
import { GenericTable } from '$components'
import { TextCell } from '$components'
import { OptionsCell } from '$components'
import { NameCell } from '$features/pets/components/cells/name-cell'
import { createColumnHelper } from '@tanstack/react-table'
import { Pet } from '$api-client/types.gen'
import { DogIcon, EyeIcon, BinIcon } from '$components'
import { ElementIntent } from '$components'
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { petDeleteMutation, clientShowQueryKey } from '$api-client/@tanstack/react-query.gen'
import { ConfirmationModal } from '$components'
import { ToastType } from '$components'
import { ToastHelper } from '$components'
import { PetsFilterParam } from '$features/pets/enums/filter-params'
import { PetRoutePaths } from '$features/pets/routes/protected'
import { Router } from '$utils/router'
import { useNavigate } from 'react-router'
import dayjs from 'dayjs'

const columnHelper = createColumnHelper<Pet>()

export type DeleteConfirmationModalState = {
  isOpen: boolean,
  petId: number | null,
}

export function PetsTab() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data } = useQuery({
    ...clientShowOptions({
      path: {
        id: Number(id),
      },
      query: {
        include_deleted: true,
        [ClientsFilterParam.IncludeUnapproved]: searchParams.get(ClientsFilterParam.IncludeUnapproved) === '1',
      },
    }),
  })

  const client = data?.data

  usePageTitle(client ? `${client.first_name} ${client.last_name} - Pets` : 'Client Pets')

  const [deleteConfirmationModalState, setDeleteConfirmationModalState] = useState<DeleteConfirmationModalState>({
    isOpen: false,
    petId: null,
  })

  const { mutate: deletePet, isPending: isDeletingPet } = useMutation({
    ...petDeleteMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: clientShowQueryKey({
          path: {
            id: Number(id),
          },
        }),
      })
      
      setDeleteConfirmationModalState({ isOpen: false, petId: null })
      ToastHelper.deleteSuccess(ToastType.Pet)
    },
    onError: () => ToastHelper.deleteError(ToastType.Pet),
  })

  const handleDeletePet = () => {
    deletePet({
      path: {
        pet: deleteConfirmationModalState.petId!,
      },
    })
  }

  const columns = [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: ({ row }) => (
        <NameCell
          data={{
            name: row.getValue('name'),
            imageUrl: row.original.image,
            fallbackIcon: DogIcon,
          }}
        />
      ),
    }),
    columnHelper.accessor('breed', {
      header: 'Breed',
      cell: ({ row }) => (
        <TextCell>
          {row.getValue('breed')}
        </TextCell>
      ),
    }),
    columnHelper.accessor('birth_date', {
      header: 'Age',
      cell: ({ row }) => (
        <TextCell>
          {dayjs.utc().diff(dayjs.utc(row.getValue('birth_date')), 'years')}
        </TextCell>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      maxSize: 66,
      minSize: 66,
      cell: ({ row }) => {
        const petViewQuery = !row.original.approved_at ? { [PetsFilterParam.IncludeUnapproved]: '1' } : undefined

        return (
          <OptionsCell
            options={[
              {
                id: 'view',
                value: 'View',
                icon: EyeIcon,
                onClick: () => navigate(Router.buildRoute(
                  Router.getRoute(PetRoutePaths.PetsView, { id: row.original.id.toString() }),
                  PetRoutePaths.PetsDetails,
                  petViewQuery,
                )),
              },
              {
                id: 'delete',
                value: 'Delete',
                icon: BinIcon,
                intent: ElementIntent.Danger,
                onClick: () => setDeleteConfirmationModalState({ isOpen: true, petId: row.original.id }),
              },
            ]}
          />
        )
      },
    }),
  ]

  if (!client) {
    return null
  }

  const hasPets = client.pets.length > 0

  if (!hasPets) {
    return <NoPetsEmptyState />
  }

  return (
    <>
      <GenericTable.Container>
        <GenericTable
          data={client.pets}
          columns={columns}
          rowLink={(pet) => Router.buildRoute(
            Router.getRoute(PetRoutePaths.PetsView, { id: pet.id.toString() }),
            PetRoutePaths.PetsDetails,
            !pet.approved_at ? { [PetsFilterParam.IncludeUnapproved]: '1' } : undefined,
          )}
        />
      </GenericTable.Container>

      {deleteConfirmationModalState.isOpen && (
        <ConfirmationModal
          intent={ElementIntent.Danger}
          onClose={() => setDeleteConfirmationModalState({ isOpen: false, petId: null })}
          onConfirm={handleDeletePet}
          isLoading={isDeletingPet}
          title="Delete Pet"
          description="Are you sure you want to delete this pet? This action cannot be undone."
        />
      )}

      <AnimatedOutlet />
    </>
  )
}
